import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useEditorState } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { flushSync } from "react-dom";
import { useWindowSize } from "usehooks-ts";

import { ChatDndPlaceholder } from "@/components/chat/ChatDndPlaceholder";
import { ChatUnreadMessagesCount } from "@/components/chat/ChatUnreadMessagesCount";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { Icon } from "@/components/icons/Icon";
import { SuggestionsContext } from "@/components/suggestions/SuggestionsProvider";
import { useSuggestionsHeight } from "@/components/suggestions/useSuggestionsHeight";
import { ImageDndArea } from "@/components/tiptap/ImageDndArea";
import { TiptapSuggestions } from "@/components/tiptap/tiptap-suggestions/TiptapSuggestions";
import { focusShowChatInput, VisibilityToggle } from "@/components/VisibilityToggle";
import {
  focusChatInput,
  getChatInputAvailableWidth,
  isChatInputEmpty,
  useFloatingChatInputWidth,
} from "@/models/chat/chatHelpers";
import { useStore } from "@/store";
import { useChats } from "@/store/useChats";
import { SCREEN_SM } from "@/style/breakpoints";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { ChatTiptap } from "../chat-tiptap/ChatTiptap";

import { ChatInputAiLogo } from "./ChatInputAiLogo";
import { ChatInputButtons } from "./ChatInputButtons";

import type { ChatModel } from "@/models/chat/ChatModel";
import type { EventMap } from "@/utils/trackEvent";

// Keep in sync with the `h-[52px]` classes on the collapsed editor below.
const COLLAPSED_INPUT_HEIGHT = 52;

// Keep in sync with the `mt-0.5` on the paragraph wrapper in Paragraph.tsx
// (ParagraphChatWrapper). Used to vertically center the first line when collapsed.
const PARAGRAPH_MARGIN_TOP = 2;

function ChatInputBar({
  chat,
  isFloatingInput = false,
  surfaceWidth: surfaceWidthOverride,
  autoFocus,
  onFocusChange,
}: {
  chat: ChatModel;
  isFloatingInput?: boolean;
  surfaceWidth?: number;
  autoFocus?: boolean;
  /** Called when the input expands (focused) or collapses. */
  onFocusChange?: (isFocused: boolean) => void;
}) {
  const t = useTranslations();
  const chats = useChats();
  const { view } = useStore();
  const { suggestionsAnchorId } = useContext(SuggestionsContext);
  const [isFocused, setIsFocused] = useState(!isFloatingInput);
  const [offset, setOffset] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const isEditorEmpty =
    useEditorState({
      editor: chat.inputDoc.editor.tiptap ?? null,
      selector: ({ editor }) => isChatInputEmpty(editor),
    }) ?? true;
  const isSendDisabled =
    chat.isUploadingFiles ||
    chat.hasFailedFiles ||
    chat.isChatSettingsSyncInProgress ||
    (isEditorEmpty && chat.uploadedFiles.length === 0);
  const isComposerEmpty = isEditorEmpty && chat.inputFiles.length === 0;
  const isCollapsed = isFloatingInput && !isFocused;
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastPointerDownAt = useRef(0);
  const suggestionsHeight = useSuggestionsHeight(
    anchorRef,
    chat.inputDoc.editor.suggestions.visible,
  );
  const bodyScrollRef = useRef<number | undefined>(undefined);

  const { width: windowWidth } = useWindowSize();
  const chatWidth = chats.chatWindowWidth;
  const isMobile = windowWidth < SCREEN_SM;
  const floatingInputWidth = useFloatingChatInputWidth();
  const dockedChatWidth = isMobile ? windowWidth : chatWidth;
  const surfaceWidth = isFloatingInput
    ? floatingInputWidth
    : (surfaceWidthOverride ?? dockedChatWidth);
  const availableWidth = getChatInputAvailableWidth(surfaceWidth);

  useEffect(() => {
    onFocusChange?.(isFocused);
  }, [isFocused, onFocusChange]);

  useEffect(() => {
    if (!isFloatingInput || !isFocused) return;

    const handler = (e: PointerEvent) => {
      // Record every pointerdown while focused so the mobile focusout handler
      // below can tell a deliberate tap on a bar control from a keyboard dismiss.
      lastPointerDownAt.current = performance.now();
      const target = e.target as HTMLElement | null;
      if (containerRef.current?.contains(target)) return;
      if (target?.closest("[data-keep-chat-input-expanded]")) return;
      if (target?.closest("[data-suggestions-box]")) return;
      setIsFocused(false);
      chat.inputDoc.editor.tiptap?.commands.blur();
    };

    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [isFloatingInput, isFocused, chat]);

  useEffect(() => {
    const container = containerRef.current;
    if (!isFocused || !container) return;
    if (!isMobile && isFloatingInput) return;

    // On mobile, dismissing the keyboard blurs the editor without any pointer
    // event on the page, so we collapse on focusout. But tapping a bar control
    // (model, etc.) also blurs the editor — there we want to stay expanded. A
    // pointerdown recorded just before the blur marks that case; 500ms covers
    // the gap between the tap and the resulting focusout.
    const handleFocusOut = () =>
      queueMicrotask(() => {
        if (performance.now() - lastPointerDownAt.current < 500) return;
        const active = document.activeElement as HTMLElement | null;
        if (container.contains(active)) return;
        if (active?.closest("[data-keep-chat-input-expanded]")) return;
        setIsFocused(false);
      });

    container.addEventListener("focusout", handleFocusOut);
    return () => container.removeEventListener("focusout", handleFocusOut);
  }, [isFocused, isMobile, isFloatingInput]);

  useEffect(() => {
    if (!isMobile) return;
    const { scrollingElement } = document;
    if (!scrollingElement) return;

    if (isFocused) {
      bodyScrollRef.current = scrollingElement.scrollTop;
    } else if (bodyScrollRef.current !== undefined) {
      scrollingElement.scrollTo({ top: bodyScrollRef.current, behavior: "smooth" });
    }
  }, [isFocused, isMobile]);

  const { tiptap } = chat.inputDoc.editor;
  useLayoutEffect(() => {
    let dom: HTMLElement | undefined;

    try {
      dom = tiptap?.view.dom;
    } catch {
      return;
    }

    if (!dom || !isCollapsed) {
      setOffset(0);
      return;
    }

    const centerFirstLine = () => {
      const firstLine = dom.firstElementChild as HTMLElement | null;
      if (!firstLine || !firstLine.offsetHeight) return;
      setOffset((COLLAPSED_INPUT_HEIGHT - firstLine.offsetHeight) / 2 - PARAGRAPH_MARGIN_TOP);
    };

    centerFirstLine();
    const resizeObserver = new ResizeObserver(centerFirstLine);
    resizeObserver.observe(dom);
    return () => resizeObserver.disconnect();
  }, [isCollapsed, tiptap]);

  useLayoutEffect(() => {
    if (isCollapsed) scrollRef.current?.scrollTo({ top: 0 });
  }, [isCollapsed]);

  useEffect(() => {
    if (offset !== 0) setTransitionEnabled(true);
  }, [offset]);

  const focusFloatingInput = () => {
    flushSync(() => setIsFocused(true));
    focusChatInput(chat);
  };

  const openChat = (source: EventMap["chat_with_bot_opened"]["source"]) => {
    trackEvent("chat_with_bot_opened", {
      source,
    });
    chats.openChat(true);
  };

  const logoStyle = {
    transition: "top 200ms, transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    transform: suggestionsHeight ? `translateY(${-suggestionsHeight + 14}px)` : undefined,
  };
  const logoContent = (
    <>
      <ChatInputAiLogo isFocused={isFocused} />
      <ChatUnreadMessagesCount pinTopRight count={chats.unreadMessagesCountForBotChat} />
    </>
  );

  const bar = (
    <div id={suggestionsAnchorId} ref={anchorRef} className="group/floating-input relative">
      <NodeStyleProvider renderContext="chat-input" availableWidth={availableWidth}>
        {chat.withBot &&
          (isFloatingInput ? (
            <button
              // Handles its own click, so the outside-pointerdown collapse must not run first
              data-keep-chat-input-expanded
              className={twMerge(
                "absolute left-2.5 bg-document-bg/80 rounded-6",
                isCollapsed ? "top-2.5" : "-top-10",
                chat.inputDoc.editor.suggestions.visible ? "z-0" : "z-61",
              )}
              style={logoStyle}
              onClick={() => openChat("floating_input_logo")}
              type="button"
              aria-label={t("chat.openInChat")}
            >
              {logoContent}
            </button>
          ) : (
            <div
              aria-hidden
              className={twMerge(
                "absolute left-2.5 -top-10 bg-document-bg/80 rounded-6 pointer-events-none",
                chat.inputDoc.editor.suggestions.visible ? "z-0" : "z-61",
              )}
              style={logoStyle}
            >
              {logoContent}
            </div>
          ))}
        {isFloatingInput && (
          <div
            data-keep-chat-input-expanded
            className={twMerge(
              "transition-opacity duration-200",
              isCollapsed
                ? twMerge(
                    "opacity-0 pointer-events-none",
                    "group-hover/floating-input:opacity-100 group-hover/floating-input:pointer-events-auto",
                    // Reachable by Tab while collapsed, so they show up when focused
                    "group-focus-within/floating-input:opacity-100 group-focus-within/floating-input:pointer-events-auto",
                  )
                : "opacity-100 pointer-events-auto",
            )}
          >
            <button
              aria-label={t("chat.openInChat")}
              className={twMerge(
                "absolute -top-9.5 right-0 flex items-center gap-0.5 px-2 h-8 text-size-14 text-secondary",
                "bg-document-bg rounded-6 shadow-block dark:shadow-block-inverted",
                "[--color-block-border:var(--color-gray-200)] dark:[--color-block-border:var(--color-gray-600)]",
                "hover:text-node-active hover:[--color-icon-main:var(--color-node-active)] hover:[--color-icon-secondary:var(--color-node-active)]",
                "after:absolute after:inset-x-0 after:top-full after:h-2 after:content-['']",
              )}
              type="button"
              onClick={() => openChat("floating_input_button")}
            >
              <Icon name="chat/OpenInChat" />
              {t("chat.openInChat")}
            </button>
            <VisibilityToggle
              className={twMerge(
                "absolute top-2.75 -right-7.75 z-1 rounded-r-6",
                "after:absolute after:inset-y-0 after:right-full after:w-px after:content-['']",
              )}
              label={t("chat.hideChatInput")}
              onClick={() => {
                view.toggleFloatingChatInput(true);
                // The input unmounts, so focus moves to the button that shows it again
                requestAnimationFrame(focusShowChatInput);
              }}
            >
              <Icon name="CrossSmall" />
            </VisibilityToggle>
          </div>
        )}
        <div
          ref={containerRef}
          className={twMerge(
            "flex flex-col gap-2 rounded-12 relative z-60 bg-chat-bg chat-input-surface",
          )}
          onFocus={() => setIsFocused(true)}
        >
          <button
            type="button"
            className="absolute top-0 left-0 w-full h-full appearance-none bg-transparent border-0 p-0 m-0"
            onClick={focusFloatingInput}
            onMouseDown={focusFloatingInput}
            // While collapsed the editor is not focusable, so this button is the Tab stop into it
            onFocus={isCollapsed ? focusFloatingInput : undefined}
            tabIndex={isCollapsed ? 0 : -1}
            aria-hidden={isCollapsed ? undefined : true}
            aria-expanded={isCollapsed ? false : undefined}
            aria-label={t("chat.focusInput")}
          />
          <ChatTiptap
            key={chat.inputDoc.id}
            rootRef={scrollRef}
            onEscape={() => setIsFocused(false)}
            chat={chat}
            label={t("chat.input")}
            doc={chat.inputDoc}
            autoFocus={isFloatingInput ? false : autoFocus}
            editorClassName={twMerge(
              "bg-chat-bg",
              transitionEnabled && "transition-[transform] duration-200",
              isCollapsed && "w-[10000px]",
            )}
            editorStyle={`transform: translateY(${offset}px)`}
            editable={!isCollapsed}
            renderPlace="chat-input"
            className={twMerge(
              "overflow-x-hidden",
              !isMobile && "transition-[min-height,max-height,margin] duration-200",
              isFocused ? "max-h-[30vh] sm:max-h-[15vh]" : "max-h-[5lh] sm:max-h-[15vh]",
              isCollapsed
                ? "min-h-[52px] max-h-[52px] m-0 -mb-11 ml-11.5 mr-18 overflow-y-hidden mask-t-from-80% mask-b-from-80% mask-r-from-80% pointer-events-none"
                : "min-h-5.5 m-3.5 mb-0 overflow-y-auto",
            )}
          />
          <ChatInputButtons
            chat={chat}
            isCollapsed={isCollapsed}
            isComposerEmpty={isComposerEmpty}
            isSendDisabled={isSendDisabled}
            isFloatingInput={isFloatingInput}
          />
        </div>
        <TiptapSuggestions doc={chat.inputDoc} chat={chat} />
      </NodeStyleProvider>
    </div>
  );

  if (!isFloatingInput) return bar;

  return (
    <ImageDndArea
      onFileDropped={(files) => {
        focusFloatingInput();
        chat.uploadFiles(files).catch((err) => {
          console.error("Failed to upload chat files:", err);
        });
      }}
      placeholder={<ChatDndPlaceholder className="flex-row" />}
    >
      {bar}
    </ImageDndArea>
  );
}

export default observer(ChatInputBar);
